import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { checkZip } from '../data/zipData';
import { playCoinSound } from '../utils/coinSound';
import { useAddressAutocomplete } from '../utils/useAddressAutocomplete';
import { fetchAllSolarData } from '../utils/solarApi';
import CAMap from '../components/CAMap';

const WEB3FORMS_KEY = '900b5fca-4b67-439d-a812-5d10f3a1cdda';

function fmt(n) { return '$' + Math.round(n).toLocaleString(); }
function fmtDec(n, d = 1) { return n.toFixed(d); }
function fmtKWh(n) { return Math.round(n).toLocaleString() + ' kWh'; }

function MonthlyChart({ data }) {
  const [hovered, setHovered] = React.useState(null);
  if (!data?.length) return null;
  const max = Math.max(...data.map(d => d.kwh));
  return (
    <div style={{ background: 'var(--gray-50)', borderRadius: 12, padding: '1.25rem', marginBottom: '1rem', border: '1px solid var(--gray-100)' }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.75rem' }}>Monthly Generation (kWh) · NREL PVWatts</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80 }}>
        {data.map(({ month, kwh }, i) => (
          <div key={month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, position: 'relative' }}
            onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
            {hovered === i && (
              <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', background: 'var(--navy)', color: '#fff', fontSize: '0.62rem', fontWeight: 700, padding: '3px 6px', borderRadius: 5, whiteSpace: 'nowrap', marginBottom: 4, zIndex: 10, pointerEvents: 'none' }}>
                {kwh.toLocaleString()}
              </div>
            )}
            <div style={{ width: '100%', borderRadius: '3px 3px 0 0', background: hovered === i ? 'var(--navy)' : 'var(--gold)', height: Math.max(3, (kwh / max) * 56), transition: 'background 0.15s' }} />
            <span style={{ fontSize: '0.58rem', color: hovered === i ? 'var(--navy)' : 'var(--gray-400)', fontWeight: 600, transition: 'color 0.15s' }}>{month.slice(0,1)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const inputStyle = { width: '100%', padding: '0.875rem 1rem', borderRadius: 10, border: '1.5px solid var(--gray-200)', fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', color: 'var(--navy)', background: 'var(--gray-50)', outline: 'none' };
const labelStyle = { display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--navy)', marginBottom: '0.4rem' };

const BUDGETS = [
  { label: '$20,000', value: 20000 }, { label: '$30,000', value: 30000 },
  { label: '$40,000', value: 40000 }, { label: '$50,000', value: 50000 },
  { label: '$60,000', value: 60000 }, { label: '$70,000', value: 70000 },
  { label: 'Above $70,000', value: 80000 },
];

export default function Residential() {
  const [form, setForm] = useState({ address: '', city: '', zip: '', monthlyBill: '', budget: '', financing: 'full', fullName: '', email: '', phone: '' });
  const [zipInfo, setZipInfo] = useState(null);
  const [consent, setConsent] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');
  const addressRef = useRef(null);

  const handleAddressSelect = useCallback(({ address, city, zip }) => {
    const info = zip.length === 5 ? checkZip(zip) : null;
    setZipInfo(info);
    setForm(f => ({ ...f, address, city, zip }));
  }, []);

  const { suggestions, open: suggestOpen, handleInput: handleAddrInput, handleSelect: handleAddrSelect, containerRef: addrContainerRef, dropdownRect, getMainText, getSecondaryText } = useAddressAutocomplete(addressRef, handleAddressSelect);

  const handleZipBlur = () => {
    if (form.zip.length === 5 && /^\d{5}$/.test(form.zip)) setZipInfo(checkZip(form.zip));
  };

  const handleSubmit = async () => {
    if (!form.monthlyBill || !form.budget) { alert('Please fill in all required fields.'); return; }
    if (!form.address) { alert('Please enter your home address.'); return; }
    if (!form.fullName.trim()) { alert('Please enter your full name.'); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) { alert('Please enter a valid email address (e.g. jane@email.com).'); return; }
    const phoneDigits = form.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) { alert('Please enter a valid 10-digit US phone number.'); return; }
    playCoinSound();
    setLoading(true); setResults(null);

    // ── 获取 NREL PVWatts + OpenEI 数据 ──
    let solarBundle = null;
    try {
      setLoadingMsg('📡 Fetching location & utility rate data...');
      solarBundle = await fetchAllSolarData(form.address, parseFloat(form.budget) / 3000, form.zip);
    } catch (e) {
      console.warn('API unavailable, using estimates:', e.message);
    }
    setLoadingMsg('⚡ Calculating your savings...');
    await new Promise(r => setTimeout(r, 600));

    const monthlyBill = parseFloat(form.monthlyBill);
    const systemCost  = parseFloat(form.budget);
    const isLowIncome  = zipInfo?.isLowIncome  || false;
    const isFireHazard = zipInfo?.isFireHazard || false;

    // 用真实电价或默认值
    const UTILITY_RATE = solarBundle?.rate?.avgRatePerKWh || 0.18;
    const utilityName  = solarBundle?.rate?.utilityName  || 'CA Average';

    // SGIP 计算（不变）
    const estimatedBatteryKWh = systemCost >= 50000 ? 27 : 13.5;
    let sgipRatePerKWh, sgipTier;
    if (isLowIncome && isFireHazard)    { sgipRatePerKWh = 1100; sgipTier = 'Equity Resiliency (DAC + Fire)'; }
    else if (isLowIncome)               { sgipRatePerKWh = 1000; sgipTier = 'Equity (DAC)'; }
    else if (isFireHazard)              { sgipRatePerKWh = 950;  sgipTier = 'Equity Resiliency (Fire Zone)'; }
    else                                { sgipRatePerKWh = 250;  sgipTier = 'General Market'; }
    const SGIPrebate   = estimatedBatteryKWh * sgipRatePerKWh;
    const netSystemCost = Math.max(systemCost - SGIPrebate, 0);

    // 用 PVWatts 年发电量，或从账单反推
    const actualAnnualKWh = solarBundle?.pv?.annualKWh || (monthlyBill / UTILITY_RATE * 12 * 0.9);
    const annualElecSavings = actualAnnualKWh * UTILITY_RATE;
    const annualNEMIncome  = monthlyBill * 12 * 0.05;
    const annualTotalBenefit = annualElecSavings + annualNEMIncome;
    const paybackYears = netSystemCost / annualTotalBenefit;
    const savings25yr  = (annualTotalBenefit * 25) - netSystemCost;

    const LOAN_RATE = 0.0699, LOAN_TERM_MONTHS = 300;
    const monthlyRate = LOAN_RATE / 12;
    const monthlyPayment = netSystemCost * (monthlyRate * Math.pow(1 + monthlyRate, LOAN_TERM_MONTHS)) / (Math.pow(1 + monthlyRate, LOAN_TERM_MONTHS) - 1);
    const monthlyNetCashFlow = (annualElecSavings / 12) + (annualNEMIncome / 12) - monthlyPayment;

    const r = {
      systemCost, SGIPrebate, sgipTier, estimatedBatteryKWh, sgipRatePerKWh,
      netSystemCost, annualElecSavings, annualNEMIncome, annualTotalBenefit,
      paybackYears, savings25yr, monthlyPayment, monthlyNetCashFlow,
      isLoan: form.financing === 'loan',
      actualAnnualKWh, utilityName, UTILITY_RATE,
      pvCapacityFactor: solarBundle?.pv?.capacityFactor,
      monthlyPV:        solarBundle?.pv?.monthlyData,
      carbonTons25yr: Math.round(actualAnnualKWh / 1000 * 0.386 * 25 * 10) / 10,
      dataSource: solarBundle ? `NREL PVWatts + ${utilityName}` : 'Estimated',
    };
    setResults(r);
    setLoading(false);
    setLoadingMsg('');

    fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        access_key: WEB3FORMS_KEY,
        subject: `[SolarSavings] New Residential Lead — ${form.fullName}`,
        from_name: form.fullName,
        replyto: form.email,
        'Form Type': 'Residential Calculator',
        Name: form.fullName, Email: form.email, Phone: form.phone,
        Address: form.address, City: form.city, ZIP: form.zip,
        'Monthly Bill': form.monthlyBill, Budget: form.budget, Financing: form.financing,
        'Net System Cost': fmt(netSystemCost),
        'SGIP Rebate': fmt(SGIPrebate), 'SGIP Tier': r.sgipTier,
        'SGIP Battery (kWh)': r.estimatedBatteryKWh, 'SGIP Rate ($/kWh)': r.sgipRatePerKWh,
        'Annual Elec Savings': fmt(annualElecSavings),
        'Annual NEM Income': fmt(annualNEMIncome),
        'Payback Years': fmtDec(paybackYears),
        '25yr Savings': fmt(savings25yr),
        'Low Income': isLowIncome ? 'Yes' : 'No',
        'Fire Zone': isFireHazard ? 'Yes' : 'No',
      }),
    }).then(res => res.json()).then(data => {
      if (!data.success) console.warn('[Web3Forms] Residential error:', data);
    }).catch(err => console.warn('[Web3Forms] Network error:', err));
  };

  return (
    <>
      <Helmet>
        <title>Solar Panels for California Homeowners – Free Savings Analysis | SolarSavings</title>
        <meta name="description" content="Find out how much you can save with residential solar in California. Free personalized analysis using real NREL data and your actual utility rate — no cost, no commitment." />
        <link rel="canonical" href="https://solarsavings.com/residential" />
      </Helmet>
      {/* HERO */}
      <div style={{ height: '42vh', minHeight: 320, background: 'linear-gradient(135deg, var(--gold) 0%, var(--gold-dark) 60%, #d4711a 100%)', display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden', padding: '0 2.5rem', paddingTop: '5rem' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'repeating-conic-gradient(rgba(255,255,255,0.05) 0% 25%, transparent 0% 50%)', backgroundSize: '40px 40px' }} />
        <div style={{ position: 'absolute', left: -80, bottom: -80, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)' }} />
        <div style={{ maxWidth: 1100, margin: '0 auto', width: '100%', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            {['✓ NREL PVWatts', '✓ Real Utility Rates', '✓ SGIP Rebate Analysis', '✓ NEM 3.0 Income Calc'].map(t => (
              <span key={t} style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(26,43,95,0.75)' }}>{t}</span>
            ))}
          </div>
          <h1 style={{ fontFamily: 'Sora, sans-serif', fontSize: 'clamp(1.8rem, 3.5vw, 2.75rem)', fontWeight: 800, color: 'var(--navy)', marginBottom: '0.75rem', lineHeight: 1.15 }}>Residential Solar ROI Calculator</h1>
          <p style={{ fontSize: '1.05rem', color: 'rgba(26,43,95,0.75)', maxWidth: 520, lineHeight: 1.65 }}>See your real savings in seconds — free, no obligation.</p>
        </div>
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(26,43,95,0.15)', backdropFilter: 'blur(4px)', padding: '0.5rem 2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(26,43,95,0.75)', margin: 0, textAlign: 'center' }}>
            * Homeowners who participate in a Prepaid PPA can still qualify for at least 30% ITC — our specialist will confirm your eligibility within 1 business day.
          </p>
        </div>
      </div>

      {/* CALCULATOR */}
      <section style={{ padding: '3rem 2.5rem 5rem', background: 'var(--gray-50)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '2rem', alignItems: 'start' }}>

          {/* FORM */}
          <div style={{ background: '#fff', borderRadius: 20, boxShadow: '0 8px 40px rgba(26,43,95,0.1)', padding: '2.5rem', border: '1px solid var(--gray-100)' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gold)', display: 'block', marginBottom: '1.5rem' }}>Tell us about your home</span>

            <div style={{ marginBottom: '1.25rem', position: 'relative' }} ref={addrContainerRef}>
              <label style={labelStyle}>Street Address *</label>
              <input
                ref={addressRef}
                style={inputStyle} type="text" placeholder="456 Sunnydale Ave"
                onChange={e => { setForm({ ...form, address: e.target.value }); handleAddrInput(e.target.value); }}
                autoComplete="off"
                spellCheck="false"
              />
              {(() => { console.log('[Dropdown] open:', suggestOpen, 'count:', suggestions.length); return null; })()}
              {suggestOpen && suggestions.length > 0 && dropdownRect && (
                <div style={{ position: 'fixed', top: dropdownRect.bottom + 4, left: dropdownRect.left, width: dropdownRect.width, background: '#fff', borderRadius: 10, boxShadow: '0 8px 30px rgba(26,43,95,0.15)', zIndex: 99999, border: '1px solid var(--gray-200)' }}>
                  {suggestions.map((s, i) => (
                    <div key={i}
                      onMouseDown={e => { e.preventDefault(); handleAddrSelect(s); }}
                      style={{ padding: '0.65rem 1rem', fontSize: '0.9rem', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)', color: 'var(--navy)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(245,166,35,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                      <span style={{ fontWeight: 600 }}>{getMainText(s)}</span>
                      <span style={{ color: 'var(--gray-400)', fontSize: '0.82rem', marginLeft: '0.4rem' }}>{getSecondaryText(s)}</span>
                    </div>
                  ))}
                </div>
              )}
              <p style={{ fontSize: '0.78rem', color: 'var(--gray-400)', marginTop: '0.3rem' }}>Start typing — we'll auto-fill your city and ZIP.</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
              <div>
                <label style={labelStyle}>City</label>
                <input style={{ ...inputStyle, background: form.city ? 'rgba(245,166,35,0.06)' : 'var(--gray-50)', borderColor: form.city ? 'var(--gold)' : 'var(--gray-200)' }} type="text" placeholder="Auto-filled" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>ZIP Code *</label>
                <input style={{ ...inputStyle, background: form.zip ? 'rgba(245,166,35,0.06)' : 'var(--gray-50)', borderColor: form.zip ? 'var(--gold)' : 'var(--gray-200)' }} type="text" maxLength={5} placeholder="Auto-filled" value={form.zip} onChange={e => setForm({ ...form, zip: e.target.value })} onBlur={handleZipBlur} />
                {zipInfo && <div style={{ fontSize: '0.78rem', marginTop: '0.3rem', color: zipInfo.sgipEligible ? 'var(--green)' : 'var(--gray-400)' }}>{zipInfo.statusMsg}</div>}
              </div>
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>Monthly Electricity Bill ($) *</label>
              <input style={inputStyle} type="number" min={50} placeholder="e.g. 400" value={form.monthlyBill} onChange={e => setForm({ ...form, monthlyBill: e.target.value })} />
              <p style={{ fontSize: '0.78rem', color: 'var(--gray-400)', marginTop: '0.3rem' }}>Check your PG&E / SCE / SDG&E bill.</p>
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>Expected Solar Investment *</label>
              <select style={inputStyle} value={form.budget} onChange={e => setForm({ ...form, budget: e.target.value })}>
                <option value="">Select your expected budget</option>
                {BUDGETS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
              <p style={{ fontSize: '0.78rem', color: 'var(--gray-400)', marginTop: '0.3rem' }}>Not sure? Pick the range that feels right — we'll refine this in your free engineer report.</p>
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>Financial Preference *</label>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.4rem' }}>
                {[['full', 'Full Payment'], ['loan', 'PPA / Prepaid']].map(([val, label]) => (
                  <label key={val} style={{ flex: 1, display: 'block', padding: '0.75rem 1rem', borderRadius: 10, border: `1.5px solid ${form.financing === val ? 'var(--gold)' : 'var(--gray-200)'}`, textAlign: 'center', cursor: 'pointer', fontSize: '0.9rem', fontWeight: form.financing === val ? 700 : 500, background: form.financing === val ? 'rgba(245,166,35,0.08)' : 'var(--gray-50)', transition: 'all 0.2s' }}>
                    <input type="radio" name="resFinancing" value={val} checked={form.financing === val} onChange={() => setForm({ ...form, financing: val })} style={{ display: 'none' }} />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            <div style={{ height: 1, background: 'var(--gray-100)', margin: '1.5rem 0' }} />

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>Full Name *</label>
              <input style={inputStyle} type="text" placeholder="Jane Smith" value={form.fullName} onChange={e => setForm({ ...form, fullName: e.target.value })} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
              <div><label style={labelStyle}>Email *</label><input style={inputStyle} type="email" placeholder="jane@email.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
              <div><label style={labelStyle}>Phone *</label><input style={inputStyle} type="tel" placeholder="(555) 000-0000" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', marginBottom: '1.25rem', padding: '1rem', background: 'var(--gray-50)', borderRadius: 10, border: `1.5px solid ${consent ? 'var(--gold)' : 'var(--gray-200)'}` }}>
              <input type="checkbox" id="resConsent" checked={consent} onChange={e => setConsent(e.target.checked)} style={{ marginTop: 2, width: 18, height: 18, accentColor: 'var(--gold)', flexShrink: 0, cursor: 'pointer' }} />
              <label htmlFor="resConsent" style={{ fontSize: '0.85rem', color: 'var(--gray-600)', lineHeight: 1.55, cursor: 'pointer' }}>Yes, I'd like to receive a free custom solar design from a certified engineer — including brand comparisons and a personalized quote. Completely free with no obligation.</label>
            </div>

            <button onClick={handleSubmit} disabled={!consent || loading}
              style={{ width: '100%', padding: '1rem', borderRadius: 50, fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: '1rem', border: 'none', cursor: consent ? 'pointer' : 'not-allowed', background: consent ? 'var(--gold)' : 'var(--gray-200)', color: consent ? 'var(--navy)' : 'var(--gray-400)', transition: 'all 0.3s' }}>
              {loading ? 'Analyzing Rooftop...' : 'Calculate My Savings →'}
            </button>
            <p style={{ textAlign: 'center', fontSize: '0.78rem', color: 'var(--gray-400)', marginTop: '0.6rem' }}>🔒 Your information is never sold or shared.</p>
          </div>

          {/* RESULTS */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {!results && !loading && <CAMap variant="gold" />}
            <AnimatePresence mode="wait">
              {loading && (
                <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  style={{ background: '#fff', borderRadius: 20, boxShadow: '0 8px 40px rgba(26,43,95,0.1)', padding: '4rem 2rem', textAlign: 'center' }}>
                  <div style={{ width: 56, height: 56, borderRadius: '50%', border: '4px solid var(--gray-200)', borderTopColor: 'var(--gold)', animation: 'spin 0.8s linear infinite', margin: '0 auto 1.25rem' }} />
                  <p style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600, color: 'var(--navy)', fontSize: '1.1rem' }}>{loadingMsg || 'Analyzing your home...'}</p>
                  <p style={{ color: 'var(--gray-400)', fontSize: '0.875rem', marginTop: '0.4rem' }}>Satellite rooftop data · Real utility rates · SGIP eligibility</p>
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </motion.div>
              )}

              {results && !loading && (
                <motion.div key="results" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                  style={{ background: '#fff', borderRadius: 20, boxShadow: '0 8px 40px rgba(26,43,95,0.1)', overflow: 'hidden' }}>
                  <div style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))', padding: '1.5rem 2rem', textAlign: 'center' }}>
                    <h3 style={{ fontFamily: 'Sora, sans-serif', color: 'var(--navy)', fontSize: '1.1rem', fontWeight: 700, textAlign: 'center' }}>Your Residential Solar Analysis</h3>
                    <p style={{ color: 'rgba(26,43,95,0.7)', fontSize: '0.8rem', marginTop: '0.2rem', textAlign: 'center' }}>Personalized for {form.fullName || 'your home'}</p>
                  </div>

                  {/* 卫星热力图 */}
                  {results.heatmapUrl && (
                    <div style={{ position: 'relative', width: '100%', height: 180, overflow: 'hidden' }}>
                      <img src={results.heatmapUrl} alt="Rooftop satellite view"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(26,43,95,0.5) 0%, transparent 60%)' }} />
                      <div style={{ position: 'absolute', bottom: 10, left: 14, fontSize: '0.7rem', color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                        📡 Satellite imagery · Google Solar API
                      </div>
                    </div>
                  )}

                  <div style={{ padding: '1.75rem' }}>

                    {/* 数据来源标签 */}
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
                      {['NREL PVWatts', results.utilityName].map(tag => (
                        <span key={tag} style={{ fontSize: '0.68rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: 20, background: 'rgba(245,166,35,0.1)', color: 'var(--gold)', border: '1px solid rgba(245,166,35,0.25)', letterSpacing: '0.04em' }}>{tag}</span>
                      ))}
                    </div>

                    {/* 年发电量 + 电价 */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', marginBottom: '1.25rem', padding: '1rem', background: 'var(--gray-50)', borderRadius: 12, border: '1px solid var(--gray-100)' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.62rem', color: 'var(--gray-400)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>Annual Generation</div>
                        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '1.05rem', color: 'var(--navy)' }}>{Math.round(results.actualAnnualKWh / 1000 * 10) / 10}k kWh</div>
                        <div style={{ fontSize: '0.62rem', color: 'var(--gray-400)' }}>NREL PVWatts</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.62rem', color: 'var(--gray-400)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>Elec Rate</div>
                        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '1.05rem', color: 'var(--navy)' }}>${results.UTILITY_RATE.toFixed(2)}/kWh</div>
                        <div style={{ fontSize: '0.62rem', color: 'var(--gray-400)' }}>{results.utilityName}</div>
                      </div>
                    </div>

                    <div style={{ background: 'linear-gradient(135deg, rgba(245,166,35,0.1), rgba(245,166,35,0.05))', border: '1.5px solid rgba(245,166,35,0.35)', borderRadius: 14, padding: '1.1rem 1.25rem', marginBottom: '1.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                        <span style={{ fontSize: '1rem' }}>⚡</span>
                        <span style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: '0.9rem', color: 'var(--navy)' }}>
                          SGIP Battery Rebate — Est. <span style={{ color: 'var(--gold-dark)' }}>{fmt(results.SGIPrebate)}</span>
                        </span>
                        <span style={{ marginLeft: 'auto', fontSize: '0.68rem', fontWeight: 700, background: 'var(--gold)', color: 'var(--navy)', padding: '0.2rem 0.6rem', borderRadius: 50, whiteSpace: 'nowrap' }}>
                          {results.sgipTier}
                        </span>
                      </div>
                      <p style={{ fontSize: '0.8rem', color: 'var(--gray-600)', lineHeight: 1.55, margin: 0 }}>
                        Based on ~{results.estimatedBatteryKWh} kWh battery storage at <strong>${results.sgipRatePerKWh}/kWh</strong>.
                        Your actual rebate may vary — <strong>a specialist will email you the confirmed amount within 1 business day.</strong>
                        {results.sgipTier === 'General Market' && (
                          <span style={{ display: 'block', marginTop: '0.4rem', color: '#b45309', fontSize: '0.75rem' }}>
                            ⚠️ General Market SGIP funds are currently oversubscribed — your application will be placed on a waitlist. Equity tier applicants (DAC/Fire zones) receive priority funding.
                          </span>
                        )}
                      </p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                      <div style={{ padding: '1.25rem', borderRadius: 12, background: 'var(--gray-50)', border: '1.5px solid var(--gray-100)' }}>
                        <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>Your Expected Investment</div>
                        <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: '1.5rem', color: 'var(--navy)' }}>{fmt(results.systemCost)}</div>
                      </div>
                      <div style={{ padding: '1.25rem', borderRadius: 12, background: 'var(--gray-50)', border: '1.5px solid var(--gray-100)' }}>
                        <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>Est. Net Cost After SGIP</div>
                        <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: '1.5rem', color: 'var(--navy)' }}>{fmt(results.netSystemCost)}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '0.25rem' }}>After ~{fmt(results.SGIPrebate)} SGIP rebate</div>
                      </div>
                    </div>

                    <div style={{ height: 1, background: 'var(--gray-100)', margin: '1.25rem 0' }} />

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                      <div style={{ padding: '1.25rem', borderRadius: 12, background: 'var(--gray-50)', border: '1.5px solid var(--gray-100)' }}>
                        <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>Estimated Annual Savings</div>
                        <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: '1.5rem', color: 'var(--navy)' }}>{fmt(results.annualTotalBenefit)}</div>
                      </div>
                      <div style={{ padding: '1.25rem', borderRadius: 12, background: 'var(--gray-50)', border: '1.5px solid var(--gray-100)' }}>
                        <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>Payback Period</div>
                        <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: '1.5rem', color: 'var(--navy)' }}>{fmtDec(results.paybackYears)} yrs</div>
                      </div>
                    </div>

                    <div style={{ padding: '1.25rem', borderRadius: 12, background: 'var(--gray-50)', border: '1.5px solid var(--gray-100)', marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>25-Year Net Savings</div>
                      <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: '1.85rem', color: 'var(--gold)' }}>{fmt(results.savings25yr)}</div>
                    </div>

                    {/* 碳减排 — 单卡：种树换算 */}
                    {results.carbonTons25yr && (
                      <div style={{ padding: '1.1rem 1.25rem', borderRadius: 12, background: 'linear-gradient(135deg, rgba(34,197,94,0.09), rgba(34,197,94,0.04))', border: '1.5px solid rgba(34,197,94,0.28)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{ fontSize: '2.2rem', lineHeight: 1, flexShrink: 0 }}>🌳</div>
                        <div>
                          <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.2rem' }}>Your Environmental Impact (25 Years)</div>
                          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '1.15rem', color: 'var(--navy)', lineHeight: 1.3 }}>
                            Equivalent to planting{' '}
                            <span style={{ color: '#16a34a' }}>{Math.round(results.carbonTons25yr * 45).toLocaleString()} trees</span>
                          </div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)', marginTop: '0.2rem' }}>
                            {results.carbonTons25yr.toLocaleString()} metric tons CO₂ avoided · EPA carbon sequestration data
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 逐月发电量图 */}
                    {results.monthlyPV?.length > 0 && <MonthlyChart data={results.monthlyPV} />}

                    {results.isLoan && (
                      <div style={{ background: 'var(--navy)', borderRadius: 12, padding: '1.25rem', marginBottom: '1rem' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <div style={{ padding: '1.25rem', borderRadius: 12, background: 'rgba(255,255,255,0.08)', border: '1.5px solid rgba(255,255,255,0.12)' }}>
                            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>Monthly Payment</div>
                            <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: '1.5rem', color: '#fff' }}>{fmt(results.monthlyPayment)}</div>
                          </div>
                          <div style={{ padding: '1.25rem', borderRadius: 12, background: 'rgba(255,255,255,0.08)', border: '1.5px solid rgba(255,255,255,0.12)' }}>
                            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>Monthly Net Cash Flow</div>
                            <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: '1.5rem', color: results.monthlyNetCashFlow >= 0 ? 'var(--green)' : 'var(--amber)' }}>
                              {(results.monthlyNetCashFlow >= 0 ? '+' : '') + fmt(results.monthlyNetCashFlow)}
                            </div>
                            {results.monthlyNetCashFlow >= 0 
                              ? <div style={{ fontSize: '0.75rem', color: 'var(--green)', marginTop: '0.25rem', fontWeight: 700 }}>You save from Day 1! 🎉</div>
                              : <div style={{ fontSize: '0.75rem', color: 'var(--amber)', marginTop: '0.25rem' }}>Break even in ~{Math.abs(Math.round(results.netSystemCost / (results.monthlyNetCashFlow)))} months</div>
                            }
                          </div>
                        </div>
                      </div>
                    )}

                    <div style={{ background: 'rgba(245,166,35,0.08)', border: '1.5px solid rgba(245,166,35,0.2)', borderRadius: 12, padding: '1rem 1.25rem', fontSize: '0.85rem', color: 'var(--navy)', lineHeight: 1.6, marginTop: '1rem' }}>
                      ⚡ Based on your location, you may qualify for additional incentives including <strong>DAC-SASH (up to $15,000)</strong> or <strong>SGIP Equity Resiliency</strong>. Our specialist will confirm within 1 business day.
                    </div>

                    <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: 'auto', paddingTop: '1.25rem', lineHeight: 1.6 }}>
                      * Homeowners who participate in a Prepaid PPA can still qualify for at least 30% ITC. Data powered by {results.dataSource}. A certified specialist will provide precise figures.
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>
    </>
  );
}
